import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] font-medium ' +
    'transition-[background-color,border-color,color,box-shadow,transform] duration-150 ' +
    'disabled:pointer-events-none disabled:opacity-50 active:scale-[0.985] ' +
    "[&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Dark ink in dark mode: the accent lightens to mint there, and white on mint is
        // unreadable. The label follows the surface rather than the surface being dulled
        // to suit a fixed label colour.
        primary: 'bg-brand text-white dark:text-ink-inverted shadow-card hover:bg-brand-hover',
        secondary: 'bg-surface text-ink border border-line-strong shadow-card hover:bg-surface-raised',
        ghost: 'text-ink-muted hover:bg-surface-raised hover:text-ink',
        subtle: 'bg-brand-soft text-brand hover:brightness-95 dark:hover:brightness-110',
        danger: 'bg-danger text-white shadow-card hover:brightness-95',
        'danger-ghost': 'text-danger hover:bg-danger-soft',
        link: 'text-brand underline-offset-4 hover:underline p-0 h-auto',
      },
      // The small sizes grow on a touch screen. 32px is a comfortable target for a
      // mouse and a poor one for a thumb — both Apple and Android ask for around 44 —
      // and a recruiter working from a phone hits the small icon buttons most of all.
      // Keyed on the pointer rather than the viewport, so a touchscreen laptop gets the
      // bigger target and a narrow desktop window does not.
      size: {
        sm: 'h-8 px-3 text-[13px] pointer-coarse:h-11 pointer-coarse:px-4',
        md: 'h-9.5 px-4 text-sm pointer-coarse:h-11',
        lg: 'h-11 px-5 text-[15px]',
        icon: 'size-9.5 p-0 pointer-coarse:size-11',
        'icon-sm': 'size-8 p-0 pointer-coarse:size-11',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, loading, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        // Announce the busy state rather than only showing a spinner.
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="size-4 animate-spin" aria-hidden />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { buttonVariants };
